import { useCallback, useEffect, useState } from 'react';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import toast from 'react-hot-toast';
import { DEFAULT_SETTINGS, settingsPayload, validateSettings } from '../utils/prospectSending';
import { phoneDigits } from '../utils/customers';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];
const WA_PROVISION_WEBHOOK = 'https://flow.seryiads.com/webhook/02c73d5f-816f-475a-9696-6a40b53c97d8';
const POLL_MS = 30000;

const isMissingTable = (error) =>
  ['42P01', 'PGRST205'].includes(error?.code) || /prospect_(settings|messages)/i.test(error?.message || '');

const newId = () => (globalThis.crypto?.randomUUID
  ? globalThis.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  }));

// Envío de la Fase 2. Cada negocio envía desde su propia instancia de WhatsApp (la que vinculó por QR en su configuración).
// Aquí solo se guardan los ajustes y se aprueban mensajes; quien envía es el flujo de n8n, con sus propias protecciones.
export const useProspectSending = () => {
  const { masterDb, activeBusinessId } = useBusinessStore();
  const { userRole, currentUserEmail } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';
  const activeBusiness = Array.isArray(masterDb) ? masterDb.find(b => String(b.id) === String(activeBusinessId)) : null;
  const instanceName = activeBusiness?.instance_name || '';

  const [settings, setSettings] = useState(null); // null = todavía no guardó nada (se usan los valores por defecto)
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [connection, setConnection] = useState('unknown'); // unknown | connected | disconnected

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!activeBusinessId) { setLoading(false); return; }
    if (!quiet) setLoading(true);
    const business = String(activeBusinessId);
    const [cfg, msgs] = await Promise.all([
      supabase.from('prospect_settings').select('*').eq('business_id', business).maybeSingle(),
      supabase.from('prospect_messages').select('*').eq('business_id', business).order('approved_at', { ascending: false }).limit(300)
    ]);
    const error = cfg.error || msgs.error;
    if (error) {
      setTableMissing(isMissingTable(error));
      setSettings(null);
      setMessages([]);
    } else {
      setTableMissing(false);
      setSettings(cfg.data || null);
      setMessages(Array.isArray(msgs.data) ? msgs.data : []);
    }
    setLoading(false);
  }, [activeBusinessId]);

  useEffect(() => { load(); }, [load]);

  const checkConnection = useCallback(async () => {
    if (!instanceName) { setConnection('disconnected'); return; }
    try {
      const response = await fetch(WA_PROVISION_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'check_status', instance_name: instanceName })
      });
      const data = await response.json();
      const open = data?.state === 'open' || data?.status === 'connected' || data?.instance?.state === 'open' || data?.connectionStatus === 'open';
      setConnection(open ? 'connected' : 'disconnected');
    } catch {
      setConnection('unknown');
    }
  }, [instanceName]);

  useEffect(() => { checkConnection(); }, [checkConnection]);

  // Mientras haya mensajes por salir se refresca solo, para ver el avance.
  const hasPending = messages.some(m => m.status === 'aprobado' || m.status === 'enviando');
  useEffect(() => {
    if (!hasPending) return undefined;
    const timer = setInterval(() => load({ quiet: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [hasPending, load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  const missingToast = () => toast.error('Falta ejecutar la migración 20260930h_prospect_sending.sql en Supabase.');

  const saveSettings = async (values) => {
    if (guardDemo()) return false;
    const problem = validateSettings(values);
    if (problem) { toast.error(problem); return false; }
    const payload = settingsPayload(activeBusinessId, values);
    const { data, error } = await supabase.from('prospect_settings').upsert(payload, { onConflict: 'business_id' }).select().maybeSingle();
    if (error) {
      if (isMissingTable(error)) missingToast(); else toast.error('No se pudieron guardar los ajustes.');
      return false;
    }
    setSettings(prev => ({ ...(prev || {}), ...(data || payload) }));
    toast.success('Ajustes guardados');
    return true;
  };

  const setSending = async (enabled) => {
    if (guardDemo()) return false;
    const base = { ...DEFAULT_SETTINGS, ...(settings || {}) };
    return saveSettings({ ...base, sending_enabled: enabled });
  };

  // Aprueba un lote: cada ítem trae el texto final exacto que se enviará.
  const approveBatch = async (items) => {
    if (guardDemo()) return false;
    const rows = (items || []).filter(i => i.ok && i.prospect?.phone);
    if (rows.length === 0) { toast.error('No hay mensajes válidos para aprobar.'); return false; }
    const batchId = newId();
    const { error } = await supabase.from('prospect_messages').insert(rows.map(({ prospect, text }) => ({
      business_id: String(activeBusinessId),
      prospect_id: prospect.id,
      phone: phoneDigits(prospect.phone),
      body: text,
      batch_id: batchId,
      approved_by: currentUserEmail || null
    })));
    if (error) {
      if (isMissingTable(error)) missingToast();
      else toast.error(error.code === '23505'
        ? 'Alguno de esos prospectos ya tiene un mensaje aprobado o enviado. Actualiza la lista e intenta de nuevo.'
        : 'No se pudo aprobar el lote.');
      return false;
    }
    await load({ quiet: true });
    toast.success(`${rows.length} mensaje${rows.length === 1 ? '' : 's'} aprobado${rows.length === 1 ? '' : 's'}`);
    return true;
  };

  const cancelWhere = async (filter, label) => {
    if (guardDemo()) return false;
    let query = supabase.from('prospect_messages').update({ status: 'cancelado' }).eq('business_id', String(activeBusinessId)).eq('status', 'aprobado');
    query = filter(query);
    const { error } = await query;
    if (error) { toast.error('No se pudo cancelar.'); return false; }
    await load({ quiet: true });
    toast.success(label);
    return true;
  };

  const cancelMessage = (id) => cancelWhere(q => q.eq('id', id), 'Mensaje cancelado');
  const cancelBatch = (batchId) => cancelWhere(q => q.eq('batch_id', batchId), 'Lote cancelado');
  const cancelPending = () => cancelWhere(q => q, 'Pendientes cancelados');

  return {
    settings, messages, loading, tableMissing, connection, instanceName, isReadOnlyDemo, hasPending,
    businessName: activeBusiness?.nombre_negocio || '',
    reload: load, checkConnection, saveSettings, setSending, approveBatch, cancelMessage, cancelBatch, cancelPending
  };
};
